import { ROUTER_PATH } from "@config/router";
import { isInvestMarketHomePath } from "@utils/investMarketRouting";

/**
 * 主壳路径规则（与 `mainTabs.tsx` + 原生 `ShellTabPathPolicy` 对齐）。
 *
 * **逻辑 Tab**（底栏高亮 / MainTabSlots）：0 Brief · 1 Invest · 2 Plan
 * **原生 WebView**（统一主壳）：0 主壳（四 Tab 合一）· 1 Chat modal（`/app-chat-modal`）
 *
 * 统一主壳 tab-0：原生不再按 path 白名单自动开 Detail（方案 A），以 Web `navigateShellAware` /
 * `open-detail` 为准；本文件仍为 Web 侧 canonical 分流与 Tab 高亮规则。
 *
 * 入参 `pathname` 应为与 `usePathname()` 一致的 **已规范化** 路径（`normalizePathname` 后）。
 */

/** 原生壳 WebView 数量：主壳 + Chat */
const SHELL_NATIVE_WEBVIEW_COUNT = 2;

/** @deprecated 使用 {@link SHELL_NATIVE_WEBVIEW_COUNT}；保留兼容旧引用 */

/** 主壳 Tab 索引（Figma Tab capsule `45482:81288` 顺序） */
export const SHELL_BRIEF_TAB_INDEX = 0;
export const SHELL_INVEST_TAB_INDEX = 1;
export const SHELL_PLAN_TAB_INDEX = 2;
/** Tab 3 Desk（原 Tracker）：与 Desk/desk 路由一致 */
export const SHELL_DESK_TAB_INDEX = 3;
/** @deprecated 使用 SHELL_DESK_TAB_INDEX */

/** 原生 Chat 常驻 WebView 的 tabIndex（`edgen_tab=1` / `shell-tab-1`） */

/** 原生统一主壳 WebView 的 tabIndex（`edgen_tab=0` / `shell-tab-0`） */

/** Tab Invest：`/invest`、`/invest/{activity|opportunity}`、`/invest/portfolio/**`；`/invest/picks` 等走 Detail。 */
export function isInvestTabInShellPath(pathname: string): boolean {
  return isInvestMarketHomePath(pathname || "");
}

/** Tab Brief：`/brief` */
export function isBriefTabInShellPath(pathname: string): boolean {
  return pathname === ROUTER_PATH.BRIEF;
}

/** Tab Plan：仅 `/plan` 根路径留在壳内；子路径走 Detail。 */
export function isPlanTabInShellPath(pathname: string): boolean {
  return (pathname || "").startsWith(ROUTER_PATH.PLAN);
}

/** Tab Desk（原 Tracker）：与 Desk/desk 路由一致 */
export function isTrackerTabInShellPath(pathname: string): boolean {
  return (pathname || "") === ROUTER_PATH.DESK;
}

/** Onboarding 全页流程：留在主壳 WebView，不走 Detail。 */
export function isOnboardingShellPath(pathname: string): boolean {
  const p = pathname || "";
  return (
    p === ROUTER_PATH.ONBOARDING || p.startsWith(`${ROUTER_PATH.ONBOARDING}/`)
  );
}

/** Welcome 前置页：留在主壳 WebView，不走 Detail。 */
export function isWelcomeShellPath(pathname: string): boolean {
  const p = pathname || "";
  return p === ROUTER_PATH.WELCOME || p.startsWith(`${ROUTER_PATH.WELCOME}/`);
}

/** 独立登录页：留在主壳 WebView，不走 Detail。Native 未登录冷启动 seed 亦为 `/login`。 */
function isLoginShellPath(pathname: string): boolean {
  const p = pathname || "";
  return p === ROUTER_PATH.LOGIN;
}

/**
 * Tab Chat modal 壳：`/app-chat-modal` 及子路径（App 独立 WebView 专用页）。
 * 全站 `/chat` 仍走 Detail 或其它容器，避免污染主 Tab。
 */
export function isChatTabInShellPath(pathname: string): boolean {
  const p = pathname || "";
  if (p === ROUTER_PATH.APP_CHAT_MODAL) {
    return true;
  }
  return p.startsWith(`${ROUTER_PATH.APP_CHAT_MODAL}/`);
}

/** 是否应留在多 Tab 壳内当前 WebView（不打开公共 Detail WebView） */
export function staysInMainTabShellWebView(pathname: string): boolean {
  return (
    isBriefTabInShellPath(pathname) ||
    isInvestTabInShellPath(pathname) ||
    isPlanTabInShellPath(pathname) ||
    isChatTabInShellPath(pathname) ||
    isOnboardingShellPath(pathname) ||
    isWelcomeShellPath(pathname) ||
    isLoginShellPath(pathname)
  );
}
